/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { UserFlagsType } from '../Login/userSlice';
import { deleteCookie, getCookieData } from '../utils';

/** Gives a cookie like:
 * {
 *   context: {
 *     patient_id: 'the-patient-id-optional',
 *     server_name: 'the-server-name-optional',
 *     encounter_id: 'the-encounter-id-optional',
 *     encrypted_mrn: 'the-encrypted-mrn',
 *     consumer_path: '/chat#foo',
 *   },
 *   auth: {
 *     accessToken:
 *       'eyJhbGciOiJSUzI1NiJ9.eyJ1c2VyX2lkIjoiOGMzYTkxOGItMjUyMi00Mzk1LWIzZTUtMzY2OGY0Y2M3MWE1Iiwib3JnX2lkIjoiYmUzZDA1NTItNDdlOC00MDNiLTk3ODMtNGQyZDNlMTQxOWM1IiwidXNlcl9mbGFncyI6eyJlZGl0X2NvbnRleHR1YWxpemF0aW9uIjp0cnVlLCJ2aWV3X2NvbnRleHR1YWxpemF0aW9uIjp0cnVlLCJhaV9zZW1hbnRpY19zZWFyY2giOnRydWUsImNhbl9naXZlX2ZlZWRiYWNrIjp0cnVlLCJjdXN0b21fTVJOX2luX2NvbnN1bWVyIjp0cnVlLCJjdXN0b21fTVJOX2luX2NyZWF0b3IiOnRydWV9LCJleHAiOjE2ODc1NDMwODJ9.lHM_pNFRzFz58Ryvio0Ini32fNdEDeugNMv0IERnTVUW3bi71Nt-lfryQXmRwSr11UQVuKMu7Xitn0PwKZxZ5yLaBMn_LTUZlI9A1sncP2sDId4Mq3Xkxd3uSA4MMZf172ao0l5QmHR-uL0ARmrb-NG8YYVyH7NLq89DNSZiOk73P3y3EFI45F17e1Abwlq4TY6D4lOZrJZuMGm5LPR1_u3vuEUVZ3wxjsXgeXYDqWC0cVxdAvFk6IEDdY92qN9bBzCNm2_hR251FgvL7NKs8agglDe1qJBDu9A3im6rPn7_Sp40pJXrmwlDz_2iwtlGgMvGPT8LudkXLr5XdNLNrQ',
 *     email: 'raul.murciano@tegria.com',
 *     familyName: 'Murciano',
 *     givenName: 'Raul',
 *     initials: 'RM',
 *     name: 'Raul Murciano',
 *     orgCode: 'PROV',
 *     orgId: 'be3d0552-47e8-403b-9783-4d2d3e1419c5',
 *     orgName: 'Providence',
 *     orgRole: 'ORG_ADMIN',
 *     title: 'Ruby Developer',
 *     userId: '8c3a918b-2522-4395-b3e5-3668f4cc71a5',
 *     userFlags: {
 *       edit_contextualization: true,
 *       custom_MRN_in_consumer: true,
 *       custom_MRN_in_creator: true,
 *     },
 *   },
 * }
 *   or if there's an error:
 * { error: string }
 */
export type SmartFhirCookieType =
  | { type: 'NEW_COOKIE'; id?: string; error?: string }
  | {
      type: 'OLD_COOKIE';
      context?: {
        patient_id?: string;
        server_name?: string;
        encounter_id?: string;
        /** This is the patient mrn encrypted with the public key by the backend.  This allows
         *  us to send the mrn, encrypted, to insecure places (like mixpanel).
         */
        encrypted_mrn?: string;
        consumer_path?: string; // path like /chat#foo to redirect to
      };
      auth?: {
        accessToken: string;
        email: string;
        familyName: string;
        givenName: string;
        initials: string;
        name: string;
        orgCode: string;
        orgId: string;
        orgName: string;
        orgRole: string;
        title: string;
        userId: string;
        userFlags: UserFlagsType;
        sessionId?: string;
      };
      error?: string;
    };

const getSmartFhirCookieData_ = () => {
  try {
    console.log('Cookie data:', JSON.stringify(document.cookie));
    const cookieData_new = getCookieData('smart_fhir_login_id');
    /* The new approach is that we store just a uuid, and use that to fetch the information we want */
    if (cookieData_new) {
      let ret = JSON.parse(window.atob(cookieData_new));
      if (ret.json) ret = ret.json;
      console.log('smart_fhir_login_id cookie found', cookieData_new, ret);
      // ret is { id: string } | { error: string }
      return { ...ret, type: 'NEW_COOKIE' } as SmartFhirCookieType;
    }

    const cookieData = getCookieData('smart-fhir');

    if (!cookieData) {
      console.log('No smart-fhir cookie found');
      return undefined;
    }
    let ret = JSON.parse(window.atob(cookieData));
    if (ret.json) ret = ret.json;
    console.log('smart-fhir cookie found', cookieData, ret);
    return { ...ret, type: 'OLD_COOKIE' } as SmartFhirCookieType;
  } catch (e) {
    console.error('Error parsing smart-fhir cookie', e);
    return undefined;
  }
};

(window as any).smartFhirCookieData = getSmartFhirCookieData_();

export const getSmartFhirCookieData = () => {
  // Make it partial since we can't really trust the cookie data
  return (window as any).smartFhirCookieData as Partial<SmartFhirCookieType> | undefined;
};

export const getSmartFhirEncryptedMrnCookieData = () => {
  const data = getSmartFhirCookieData();
  if (data?.type === 'OLD_COOKIE') {
    return data?.context?.encrypted_mrn;
  }
  return undefined;
};

export const deleteSmartFhirCookieData = () => {
  (window as any).smartFhirCookieData = undefined;
  deleteCookie('smart-fhir');
};
